import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";

export type PlotDraft = components["schemas"]["PlotDraft"];

export type UsePlotDraftsResult = UseQueryResult<PlotDraft[], ApiError>;

/**
 * worldline-palimpsest (Phase 3-1, Task 4.2) `GET /scenarios/{scenarioId}/plot-drafts` hook。
 *
 * palimpsest 2D view の骨格 beat マーカー描画に利用する。該当シナリオに PlotDraft が
 * 0 件でも 200 空配列が返るため、エラーにはしない (Req 3.3)。
 *
 * - cache key: `['plotDrafts', scenarioId]`
 * - staleTime: Infinity (PlotDraft は creator が再生成するまで不変)
 * - retry: 無効 (既存 hook 命名規則に従う)
 * - scenarioId が null のときは enabled: false でクエリを実行しない
 */
export function usePlotDrafts(scenarioId: string | null): UsePlotDraftsResult {
  const client = useApiClient();

  return useQuery<PlotDraft[], ApiError>({
    queryKey: ["plotDrafts", scenarioId],
    enabled: scenarioId !== null,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      if (scenarioId === null) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "usePlotDrafts queryFn invoked without scenarioId",
        });
      }

      const { data, error } = await client.GET(
        "/scenarios/{scenarioId}/plot-drafts",
        {
          params: { path: { scenarioId } },
        },
      );

      if (error !== undefined) {
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "listPlotDraftsForScenario returned no data",
        });
      }
      return data;
    },
  });
}
